/**
 * The link between the screen page and the NoorView TV app (Android). Inside the app, `window.NoorTV` is provided by
 * the app itself; in a browser it's missing and everything here does nothing, so the same page works in both.
 */

interface NativeTV {
  /** The screen is paired and locked: the app keeps itself open and in front. */
  setLocked(locked: boolean): void;
  /** Close the app (only when the screen isn't locked). */
  exit(): void;
  /** "Still running": the app reloads the page if these stop (a frozen page). */
  alive(): void;
  /** JSON: TvInfo. */
  info(): string;
  /** Opens the system setting that lets NoorView start when the TV turns on. */
  openAutostartSettings(): void;
  /** The page has drawn: the app removes its own start-up picture. */
  shown(): void;
}

export interface TvInfo {
  /** Which app: the TV app (Android) or NoorView Screen for Windows. Missing in older TV apps (Android). */
  platform?: 'android' | 'windows';
  /** The app's version, e.g. "1.0.0 (12)". */
  version: string;
  /** The TV's model, e.g. "AFTKA" or "Sony BRAVIA 4K". */
  model: string;
  /** The picture's real size in pixels, e.g. "3840 × 2160". */
  resolution: string;
  /** Whether NoorView can open by itself when the TV turns on (some Android versions need a setting for this). */
  autostart: boolean;
  /** "user": someone opened NoorView (shows the start-up card). "auto": the TV turned on, or the app recovered. */
  launch: 'user' | 'auto';
}

const native = (window as unknown as { NoorTV?: NativeTV }).NoorTV;

/** True inside the NoorView TV app. */
export const inTvApp = !!native;

export function tvInfo(): TvInfo | null {
  if (!native) return null;
  try { return JSON.parse(native.info()) as TvInfo; } catch { return null; }
}

export const tv = {
  setLocked(on: boolean) { try { native?.setLocked(on); } catch { /* ignore */ } },
  exit() { try { native?.exit(); } catch { /* ignore */ } },
  alive() { try { native?.alive(); } catch { /* ignore */ } },
  openAutostartSettings() { try { native?.openAutostartSettings(); } catch { /* ignore */ } },
  shown() { try { native?.shown(); } catch { /* ignore */ } },
};

/** What the remote's Back button does (the app calls window.__noorTvBack). The latest handler wins. */
export function onTvBack(fn: () => void) {
  const w = window as unknown as { __noorTvBack?: () => void };
  w.__noorTvBack = fn;
  return () => { if (w.__noorTvBack === fn) delete w.__noorTvBack; };
}
